import { resolveHotelProfilePackageBookingSsot } from "./agentHotelProfilePricing";
import { buildHotelBookingPricingSnapshotAtLock } from "./resolveHotelBookingSummaryPricingSsot";
import { roundMoney2 } from "../pricing/pricingEngine";
import {
  tryBuildHotelApiPriceOnlyDraftPatch,
  PRICE_PIPELINE_MODE_HOTEL_API_ONLY,
} from "./hotelApiPriceOnlyPipeline";
import { isAgentHotelCustomerFacingPricingEnabled } from "./pricing/resolveAgentHotelCustomerFacingPriceIls";
import { getHotelProfilePackageSupplierUsd } from "./hotelSellingPrice";
import {
  enrichComboPackageWithRoomsForPricing,
  isHotelProfileMultiRoomComboPackage,
  resolveHotelProfileMultiRoomComboBookingSsot,
  resolveMultiRoomComboCustomerFacingTotalFromOrder,
} from "./pricing/resolveAgentHotelMultiRoomPricingDisplay";
import { buildProfilePriceContextFromOrder } from "./hotelBooking/traceHotelMultiRoomPriceChain";

const AGENT_HR_PRICING_SOURCE = "agent_hotel_customer_facing_ssot";

function finitePositive(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function orderHasAgentHrPricingSource(order) {
  const sources = [
    order?.agentOurPrice?.pricingSource,
    order?.ourPrice?.pricingSource,
    order?.selectedHotelPackage?.ourPrice?.pricingSource,
  ];
  if (sources.some((s) => s === AGENT_HR_PRICING_SOURCE)) return true;
  const trace =
    order?.bookingSsotTrace ?? order?.selectedPriceSnapshot?.bookingSsotTrace ?? null;
  const pipeline = String(trace?.pipeline ?? trace?.source ?? "");
  return pipeline.includes("agent_hr");
}

/** Agent HR customer line locked on ourPrice/agentOurPrice — not supplier displayCurrencies. */
function resolveAgentHrCustomerLineIlsFromOrder(order) {
  const agentOur = order?.agentOurPrice;
  if (agentOur?.pricingSource === AGENT_HR_PRICING_SOURCE) {
    const n = finitePositive(agentOur.priceWithCommission);
    if (n != null) return roundMoney2(n);
  }
  const ourPrice = order?.ourPrice ?? order?.selectedHotelPackage?.ourPrice ?? null;
  if (ourPrice?.pricingSource === AGENT_HR_PRICING_SOURCE) {
    const n = finitePositive(ourPrice.priceWithCommission);
    if (n != null) return roundMoney2(n);
  }
  return null;
}

function normalizePkgRoomsArray(pkg) {
  const rooms = pkg?.Rooms;
  if (Array.isArray(rooms)) return rooms;
  if (rooms && typeof rooms === "object") return Object.values(rooms);
  return [];
}

function packageHasMissingPerRoomPrices(pkg) {
  const rooms = normalizePkgRoomsArray(pkg);
  if (rooms.length < 2) return false;
  return rooms.some((entry) => {
    const n = Number(
      entry?.Price?.FinalPrice ??
        entry?.price?.FinalPrice ??
        entry?.FinalPrice ??
        entry?.finalPrice
    );
    return !Number.isFinite(n) || n <= 0;
  });
}

function resolvePackageIdentity(pkg) {
  return String(pkg?.packageID ?? pkg?.PackageId ?? pkg?.packageId ?? "");
}

/**
 * Multi-room combo SSOT — sum(per-room customerFacing), never first-room-only bookingSsotIls.
 * @returns {{ bookingSsotIls: number, trace: object }|null}
 */
export function resolveMultiRoomOrderDraftBookingSsot({
  selectedPackage,
  selectedRoom,
  profilePriceContext,
  multiRoomProfileSelections = null,
  selectedPackageOriginalSnapshot = null,
}) {
  const selectedPackageId = resolvePackageIdentity(selectedPackage);
  const snapshotPackageId = resolvePackageIdentity(selectedPackageOriginalSnapshot);
  const snapshotMatchesSelected =
    selectedPackageOriginalSnapshot &&
    selectedPackageId &&
    snapshotPackageId &&
    selectedPackageId === snapshotPackageId;
  const shouldUseSnapshotForComboSsot = Boolean(
    snapshotMatchesSelected && packageHasMissingPerRoomPrices(selectedPackageOriginalSnapshot)
  );
  const comboSelectedPackage = shouldUseSnapshotForComboSsot
    ? selectedPackageOriginalSnapshot
    : selectedPackage;

  const orderLike = {
    selectedHotelPackage: comboSelectedPackage,
    selectedRoom,
    ...(selectedPackageOriginalSnapshot
      ? { selectedPackageOriginalSnapshot }
      : {}),
    ...(Array.isArray(multiRoomProfileSelections)
      ? { multiRoomProfileSelections }
      : {}),
  };

  if (isHotelProfileMultiRoomComboPackage(selectedRoom, comboSelectedPackage, orderLike)) {
    const enrichedPkg = enrichComboPackageWithRoomsForPricing(
      orderLike,
      selectedRoom,
      comboSelectedPackage
    );
    const comboSsot = resolveHotelProfileMultiRoomComboBookingSsot({
      room: selectedRoom,
      pkg: enrichedPkg,
      profilePriceContext,
      order: orderLike,
    });
    if (comboSsot?.bookingSsotIls != null) {
      return {
        bookingSsotIls: comboSsot.bookingSsotIls,
        trace: {
          ...comboSsot.trace,
          source: shouldUseSnapshotForComboSsot
            ? "hotel_profile_combo_package_snapshot_total"
            : comboSsot.trace?.source ?? "hotel_profile_combo_package",
          comboTotal: comboSsot.bookingSsotIls,
          selectedPackageId: selectedPackageId || null,
          snapshotPackageId: snapshotPackageId || null,
          usedSnapshotPackageTotal: shouldUseSnapshotForComboSsot,
        },
        agentOurPrice: comboSsot.agentOurPrice ?? null,
      };
    }
  }

  const comboTotal = resolveMultiRoomComboCustomerFacingTotalFromOrder(
    orderLike,
    profilePriceContext
  );
  if (comboTotal != null) {
    return {
      bookingSsotIls: comboTotal,
      trace: {
        pipeline: "agent_hr_multi_room_combo_ssot",
        source: shouldUseSnapshotForComboSsot
          ? "hotel_profile_combo_package_snapshot_total"
          : "sum_per_room_customer_facing",
        chosenSupplierTotalSource:
          orderLike?.selectedHotelPackage?.trace?.chosenSupplierTotalSource ??
          null,
        chosenSupplierTotalUsd:
          orderLike?.selectedHotelPackage?.trace?.chosenSupplierTotalUsd ??
          null,
        supplierNetIlsInput:
          orderLike?.selectedHotelPackage?.trace?.supplierNetIlsInput ?? null,
        supplierNetIlsOutput:
          orderLike?.selectedHotelPackage?.trace?.supplierNetIlsOutput ?? null,
        usedDisplayCurrenciesIls:
          orderLike?.selectedHotelPackage?.trace?.usedDisplayCurrenciesIls ??
          false,
        usedPackagePriceFinalPrice:
          orderLike?.selectedHotelPackage?.trace?.usedPackagePriceFinalPrice ??
          false,
        usedRoomsSumFinalPrice:
          orderLike?.selectedHotelPackage?.trace?.usedRoomsSumFinalPrice ??
          false,
        fallbackReason:
          orderLike?.selectedHotelPackage?.trace?.fallbackReason ?? null,
        comboTotal,
        selectedPackageId: selectedPackageId || null,
        snapshotPackageId: snapshotPackageId || null,
        usedSnapshotPackageTotal: shouldUseSnapshotForComboSsot,
      },
    };
  }

  return null;
}

/**
 * שורת מלון בלקוח (ILS) — bookingSsotIls / snapshot.finalPriceIls.
 * Multi-room: always sum(per-room HR SSOT), never stale first-room bookingSsotIls.
 * Agent HR: when order.bookingSsotIls is stale supplier (hotel_api_price_only), prefer HR ourPrice line.
 * @param {object|null|undefined} order
 * @returns {number|null}
 */
export function resolveCustomerFacingHotelLineIlsFromOrder(order) {
  if (!order || typeof order !== "object") return null;

  const multiComboTotal = resolveMultiRoomComboCustomerFacingTotalFromOrder(
    order,
    buildProfilePriceContextFromOrder(order)
  );
  if (multiComboTotal != null) {
    return roundMoney2(multiComboTotal);
  }

  const rootRaw =
    order.bookingSsotIls ?? order.selectedPriceSnapshot?.finalPriceIls ?? null;
  const rootBooking = finitePositive(rootRaw);
  const rootRounded = rootBooking != null ? roundMoney2(rootBooking) : null;

  if (orderHasAgentHrPricingSource(order)) {
    const hrLine = resolveAgentHrCustomerLineIlsFromOrder(order);
    if (hrLine != null && (rootRounded == null || hrLine > rootRounded + 0.5)) {
      return hrLine;
    }
  }

  return rootRounded;
}

/**
 * יוצר bookingSsotIls ממסלול הפרופיל (שווה-ערך ל־formatHotelProfilePackagePriceLine),
 * ממזג ל־selectedPriceSnapshot.finalPriceIls ולוגים ב־DEV.
 * לא משנה את selectedPackage.Rooms — זהות חדרי הספק נשמרת בנפרד (snapshot לפני בוקינג).
 */
export function mergeHotelOrderDraftBookingSsot({
  selectedPackage,
  selectedRoom,
  selectedPriceSnapshot,
  profilePriceContext,
  multiRoomProfileSelections = null,
  selectedPackageOriginalSnapshot = null,
}) {
  const roomId =
    selectedRoom?.id ??
    selectedRoom?.roomId ??
    selectedRoom?.RoomId ??
    null;

  const multiRoomSsot = resolveMultiRoomOrderDraftBookingSsot({
    selectedPackage,
    selectedRoom,
    profilePriceContext,
    multiRoomProfileSelections,
    selectedPackageOriginalSnapshot,
  });

  const singleRoomSsot = resolveHotelProfilePackageBookingSsot(
    selectedPackage,
    profilePriceContext,
    { roomId, selectedRoom }
  );

  const bookingSsotIls =
    multiRoomSsot?.bookingSsotIls ?? singleRoomSsot.bookingSsotIls;
  const trace = multiRoomSsot?.trace ?? singleRoomSsot.trace;
  const agentOurPrice = multiRoomSsot?.agentOurPrice ?? singleRoomSsot.agentOurPrice;

  const pricingSnapshot =
    bookingSsotIls != null
      ? buildHotelBookingPricingSnapshotAtLock({
          selectedPackage,
          profilePriceContext,
          bookingSsotIls,
          bookingSsotTrace: trace,
        })
      : null;

  const mergedSnapshot =
    selectedPriceSnapshot && typeof selectedPriceSnapshot === "object"
      ? {
          ...selectedPriceSnapshot,
          finalPriceIls: bookingSsotIls,
          bookingSsotTrace: trace,
          ...(pricingSnapshot ? { pricingSnapshot } : {}),
        }
      : bookingSsotIls != null
        ? {
            finalPriceIls: bookingSsotIls,
            bookingSsotTrace: trace,
            selectedAt: Date.now(),
            ...(pricingSnapshot ? { pricingSnapshot } : {}),
          }
        : selectedPriceSnapshot;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info("[HOTEL_SELECTED_ROOM_PRICE_NAV_TRACE]", {
      ...trace,
      bookingSsotIls,
      selectedPriceSnapshotHadData: Boolean(selectedPriceSnapshot),
    });
  }

  return {
    bookingSsotIls,
    selectedPriceSnapshot: mergedSnapshot,
    bookingSsotTrace: trace,
    pricingSnapshot,
    agentOurPrice: agentOurPrice ?? null,
  };
}

export const ORDER_DRAFT_BOOKING_SSOT_CREATE_TRACE =
  "[ORDER_DRAFT_BOOKING_SSOT_CREATE_TRACE]";

/** @param {Record<string, unknown>} payload */
export function logOrderDraftBookingSsotCreateTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(ORDER_DRAFT_BOOKING_SSOT_CREATE_TRACE, payload);
    if (payload.status === "FAIL") {
      // eslint-disable-next-line no-console
      console.error(`${ORDER_DRAFT_BOOKING_SSOT_CREATE_TRACE}:FAIL`, payload);
    }
  } catch {
    /* ignore */
  }
}

/**
 * SSOT at room lock — agent HR bookingSsotIls wins over hotel_api_price_only supplier lock.
 * @param {{
 *   selectedPackage: object,
 *   selectedRoom: object,
 *   selectedPriceSnapshot?: object|null,
 *   profilePriceContext: object,
 *   draftCtx?: object,
 *   traceMeta?: { caller?: string, locationState?: object|null, sessionStorageKey?: string },
 * }} params
 */
export function buildHotelOrderDraftBookingSsotAtRoomLock({
  selectedPackage,
  selectedRoom,
  selectedPriceSnapshot = null,
  profilePriceContext,
  draftCtx = {},
  traceMeta = {},
}) {
  const pkg = selectedPackage;
  const packageId = pkg?.packageID ?? pkg?.PackageId ?? pkg?.packageId ?? null;
  const displayCurrenciesIls = finitePositive(pkg?.displayCurrencies?.ils);
  const hrPercent =
    profilePriceContext?.agentHotelPricing?.hotelHrCommissionPercentage ?? null;
  const supplierNetRaw = getHotelProfilePackageSupplierUsd(pkg);
  const supplierNetIls = displayCurrenciesIls ?? finitePositive(supplierNetRaw);

  const ssotMerge = mergeHotelOrderDraftBookingSsot({
    selectedPackage: pkg,
    selectedRoom,
    selectedPriceSnapshot,
    profilePriceContext,
    multiRoomProfileSelections: draftCtx.multiRoomProfileSelections ?? null,
    selectedPackageOriginalSnapshot:
      draftCtx.selectedPackageOriginalSnapshot ??
      selectedPriceSnapshot?.selectedPackageOriginalSnapshot ??
      null,
  });
  const expectedBookingSsotIls = ssotMerge.bookingSsotIls;
  const agentHrEnabled = isAgentHotelCustomerFacingPricingEnabled(profilePriceContext);

  const pipelinePatch = tryBuildHotelApiPriceOnlyDraftPatch(pkg, draftCtx);
  const apiOnlyDraftSsotIls = pipelinePatch?.bookingSsotIls ?? null;

  let actualBookingSsotIls = null;
  let sourceResolver = "none";
  let pricePipelineMode;
  let selectedPriceSnapshotOut = ssotMerge.selectedPriceSnapshot;
  let pricingSnapshotOut = ssotMerge.pricingSnapshot ?? null;
  let agentOurPriceOut = ssotMerge.agentOurPrice ?? null;
  let bookingSsotTraceOut = ssotMerge.bookingSsotTrace ?? null;

  if (agentHrEnabled && expectedBookingSsotIls != null) {
    actualBookingSsotIls = expectedBookingSsotIls;
    sourceResolver = "agent_hr_customer_facing_ssot";
    pricePipelineMode = pipelinePatch?.pricePipelineMode;
    const baseSnap =
      pipelinePatch?.selectedPriceSnapshot ??
      (selectedPriceSnapshot && typeof selectedPriceSnapshot === "object"
        ? selectedPriceSnapshot
        : {});
    selectedPriceSnapshotOut = {
      ...baseSnap,
      ...ssotMerge.selectedPriceSnapshot,
      finalPriceIls: expectedBookingSsotIls,
      bookingSsotTrace: ssotMerge.bookingSsotTrace,
      ...(pricingSnapshotOut ? { pricingSnapshot: pricingSnapshotOut } : {}),
      ...(pipelinePatch
        ? { pricePipelineMode: PRICE_PIPELINE_MODE_HOTEL_API_ONLY }
        : {}),
    };
  } else if (pipelinePatch) {
    actualBookingSsotIls = apiOnlyDraftSsotIls;
    sourceResolver = "displayCurrencies.ils";
    pricePipelineMode = pipelinePatch.pricePipelineMode;
    pricingSnapshotOut =
      apiOnlyDraftSsotIls != null
        ? buildHotelBookingPricingSnapshotAtLock({
            selectedPackage: pkg,
            profilePriceContext,
            bookingSsotIls: apiOnlyDraftSsotIls,
          })
        : null;
    selectedPriceSnapshotOut = {
      ...pipelinePatch.selectedPriceSnapshot,
      ...(pricingSnapshotOut ? { pricingSnapshot: pricingSnapshotOut } : {}),
    };
    agentOurPriceOut = null;
    bookingSsotTraceOut = null;
  } else {
    actualBookingSsotIls = expectedBookingSsotIls;
    sourceResolver = expectedBookingSsotIls != null ? "mergeHotelOrderDraftBookingSsot" : "none";
    selectedPriceSnapshotOut = ssotMerge.selectedPriceSnapshot;
    pricingSnapshotOut = ssotMerge.pricingSnapshot ?? null;
    agentOurPriceOut = ssotMerge.agentOurPrice ?? null;
    bookingSsotTraceOut = ssotMerge.bookingSsotTrace ?? null;
  }

  let status = "SKIP";
  if (expectedBookingSsotIls != null && actualBookingSsotIls != null) {
    status =
      Math.abs(Number(expectedBookingSsotIls) - Number(actualBookingSsotIls)) <= 0.5
        ? "PASS"
        : "FAIL";
  } else if (!agentHrEnabled && actualBookingSsotIls != null) {
    status = "PASS";
  }

  const draftTrace = {
    packageId,
    displayCurrenciesIls,
    supplierNetIls,
    hrPercent,
    expectedBookingSsotIls,
    actualBookingSsotIls,
    apiOnlyDraftSsotIls,
    sourceResolver,
    locationState: traceMeta.locationState ?? null,
    sessionStorageKey: traceMeta.sessionStorageKey ?? "currentHotelOrder",
    caller: traceMeta.caller ?? null,
    agentHrEnabled,
    status,
  };
  logOrderDraftBookingSsotCreateTrace(draftTrace);

  return {
    bookingSsotIls: actualBookingSsotIls,
    selectedPriceSnapshot: selectedPriceSnapshotOut,
    pricingSnapshot: pricingSnapshotOut,
    pricePipelineMode,
    agentOurPrice: agentOurPriceOut,
    bookingSsotTrace: bookingSsotTraceOut,
    draftTrace,
  };
}
